import i18n from "i18next";
import k from "src/i18n/keys";

import React, {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  useEffect,
  useMemo,
} from "react";
import * as R from "ramda";
import TextareaAutosize from "react-textarea-autosize";
import { useSelector, useDispatch } from "react-redux";
import { Text as ChakraText } from "@chakra-ui/react";
import Linkify from "react-linkify";

import {
  TextFieldContainer,
  Done,
  Discard,
  TextFieldButtons,
  IconContainer,
} from "../styles";
import { TextField } from "src/components/Dock/Checklist/Text/styles";
import {
  DisplayContainer,
  Display,
} from "src/components/Dock/Checklist/ts-styles";
import useKeyDownHandlers from "src/hooks/useKeyDownHandlers";
import OutsideClickHandler from "src/components/OutsideClickHandler";
import { getSettings } from "src/utils/checklist";
import { getChecklistNotificationAuthor } from "src/utils/chatroom";
import AddData from "../AddData";
import TextFieldUpdate from "./TextFieldUpdate";

import {
  getChecklistValue,
  getChecklistFieldDetails,
  getWhetherMandatoryField,
  getLockedStatus,
  getChecklistFormValue,
  getIsFieldLocked,
  getFormFieldMandatoryStatus,
  getSelectedChecklist,
  isProcessRowSelected,
  getLastChat,
  getCurrentUserId,
  getCurrentRoomId,
} from "src/reducers";
import {
  updateChecklistFromManageView,
  bulkUpdateProcess,
} from "src/actions/workflows";
import { setChecklistValue } from "src/actions/checklist";
import { extractChecklistValue, getPlaceholder } from "src/utils/checklist";

import useFields from "../useFields";
import Icon from "src/icons";

import type {
  AppState,
  FieldId,
  RoomId,
  ReactRef,
  ColumnId,
  FieldValue,
  HttpMethods,
} from "src/types";

type Props = {
  formId?: number | null;
  roomId: RoomId;
  fieldId: FieldId;
  roomFieldFormId?: string;
  fromManageView?: boolean;
  handleClose?: () => void;
};

const Text = ({
  formId,
  roomId,
  fieldId,
  roomFieldFormId,
  fromManageView = false,
  handleClose,
}: Props) => {
  const textAreaRef: ReactRef = useRef(null);
  const dispatch = useDispatch();

  const isChecklistFieldMandatory = useSelector(({ app }: { app: AppState }) =>
    getWhetherMandatoryField(app, roomId, fieldId),
  );

  const isFormFieldMandatory = useSelector(({ app }: { app: AppState }) =>
    getFormFieldMandatoryStatus(app, roomFieldFormId ?? ""),
  );
  const isMandatory = formId ? isFormFieldMandatory : isChecklistFieldMandatory;

  const isChecklistFieldLocked = useSelector(({ app }: { app: AppState }) =>
    getLockedStatus(app, roomId, fieldId),
  );
  const isFormFieldLocked = useSelector(({ app }: { app: AppState }) =>
    getIsFieldLocked(app, roomFieldFormId ?? null, fieldId, roomId),
  );
  const locked = formId ? isFormFieldLocked : isChecklistFieldLocked;

  const currentRoomId = useSelector(({ app }: { app: AppState }) =>
    getCurrentRoomId(app),
  );

  const {
    columnId,
    value: selectedValue,
    roomId: selectedRoomId,
    index,
    embeddedIndex,
  } = useSelector(({ app }: { app: AppState }) => getSelectedChecklist(app));

  const [selectedFieldValue, setSelectedFieldValue] = useState(selectedValue);

  const rowSelected: boolean = useSelector(({ app }: { app: AppState }) =>
    isProcessRowSelected(app, selectedRoomId),
  );

  const originalRoomId = fromManageView ? selectedRoomId : roomId;
  const checklistFieldValue = useSelector(({ app }: { app: AppState }) =>
    getChecklistValue(app, fieldId, originalRoomId),
  );
  const formFieldValue = useSelector(({ app }: { app: AppState }) =>
    getChecklistFormValue(app, roomFieldFormId ?? ""),
  );

  const checklistValue = formId ? formFieldValue : checklistFieldValue;

  const details = useSelector(({ app }: { app: AppState }) =>
    getChecklistFieldDetails(app, `${fieldId}`),
  );

  const placeholder = getPlaceholder(
    getSettings(details?.get("settings")),
    i18n.t(k.ENTER_TEXT),
  );

  const lastMessage = useSelector(({ app }: { app: AppState }) =>
    getLastChat(app, roomId),
  );
  const currentUserId = useSelector(({ app }: { app: AppState }) =>
    getCurrentUserId(app),
  );
  const checklistUpdatedByOtherUser = useMemo(
    () => getChecklistNotificationAuthor(lastMessage) !== currentUserId,
    [lastMessage, currentUserId],
  );

  const {
    edit,
    modified,
    value: extractedValue,
    setValue,
    handleChange,
    openEdit,
    closeEdit,
    settings,
  } = useFields({
    checklistValue,
    details,
    checklistUpdated: checklistUpdatedByOtherUser,
  });

  const value = fromManageView ? selectedFieldValue : extractedValue;

  const multiple = settings?.multiple || false;

  const handleSelectedChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setSelectedFieldValue(e.target.value);
    },
    [],
  );

  useEffect(() => {
    setSelectedFieldValue(selectedValue);
  }, [selectedValue]);

  // Updating this method and resolving type warnings requires updating
  // the bulk update logic for this component
  const setChecklistValueFromManageView = useCallback(
    ({
      id: fieldId,
      value: fieldDetail,
      httpMethod,
      extraBody = null,
      columnId,
    }: {
      id: FieldId;
      value: FieldValue;
      httpMethod: HttpMethods | undefined;
      extraBody?: Record<string, unknown> | undefined | null;
      columnId?: ColumnId;
    }) => {
      const { value, type } = fieldDetail;
      if (rowSelected) {
        if (!multiple) {
          return bulkUpdateProcess({
            attrs: {
              [fieldId]: value,
            },
          });
        } else {
          setSelectedFieldValue(value);
        }
      } else {
        return updateChecklistFromManageView(
          selectedRoomId,
          {
            [fieldId]: value,
            type,
            value,
          },
          index,
          fieldId,
          httpMethod,
          extraBody || {},
          columnId ?? "",
          embeddedIndex,
        );
      }

      if (!multiple && rowSelected && handleClose) {
        handleClose();
      }
    },
    [rowSelected, multiple, selectedRoomId, index, embeddedIndex, handleClose],
  );

  const setChecklistFieldValue = useCallback(
    ({
      roomId,
      id,
      value,
      formId,
      httpMethod,
      extraBody,
    }: {
      roomId: RoomId;
      id: FieldId;
      value: FieldValue;
      progress: boolean;
      formId?: number;
      httpMethod?: HttpMethods;
      extraBody?: Record<string, unknown>;
    }) => {
      if (fromManageView) {
        return setChecklistValueFromManageView({
          id,
          value,
          httpMethod,
          extraBody,
          columnId,
        });
      } else {
        return setChecklistValue({
          roomId,
          id: fieldId,
          value,
          progress: true,
          formId: formId ?? null,
        });
      }
    },
    [fromManageView, fieldId, columnId, setChecklistValueFromManageView],
  );

  const handleClick = useCallback(() => {
    if (locked) return;
    openEdit();
  }, [locked, openEdit]);

  const componentDecorator = useCallback(
    (href: string, text: string, key: number) => (
      <a
        href={href}
        key={key}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {text}
      </a>
    ),
    [],
  );

  const updateChecklistValue = useCallback(() => {
    const prevValue = extractChecklistValue(checklistValue);

    if (prevValue !== value) {
      const action = setChecklistFieldValue({
        roomId,
        id: fieldId,
        value: {
          value: value === "" ? null : value,
          type: "date",
          checked: !!value,
        },
        progress: true,
        formId: formId,
      });
      if (action) {
        dispatch(action);
      }
    }

    closeEdit();
  }, [
    roomId,
    formId,
    fieldId,
    value,
    checklistValue,
    dispatch,
    setChecklistFieldValue,
    closeEdit,
  ]);

  useLayoutEffect(() => {
    if (edit && textAreaRef.current)
      textAreaRef.current.setSelectionRange(
        (value || "").length,
        (value || "").length,
      );
  }, [edit]);

  const { handleShiftEnter } = useKeyDownHandlers(updateChecklistValue);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.keyCode === 27 && setValue) {
      handleDiscardValue();
    } else {
      handleShiftEnter(e);
    }
  };

  const handleDiscardValue = useCallback(() => {
    const prevValue = extractChecklistValue(checklistValue);
    if (setValue) {
      setValue(prevValue);
      if (closeEdit) {
        closeEdit();
      }
    }
  }, [checklistValue, closeEdit, setValue]);

  if (edit) {
    return (
      <OutsideClickHandler onClickOutside={updateChecklistValue}>
        <TextFieldContainer>
          <TextField disabled={locked} modified={modified}>
            <TextareaAutosize
              data-cy={`checklist-text-input`}
              placeholder={placeholder}
              value={value || ""}
              onChange={fromManageView ? handleSelectedChange : handleChange}
              autoFocus
              ref={(el) => {
                textAreaRef.current = el;
              }}
              onKeyDown={handleKeyDown}
              readOnly={locked}
              disabled={locked}
            />
            <TextFieldButtons>
              {modified ? (
                <Discard
                  data-cy={`checklist-text-discard`}
                  onClick={handleDiscardValue}
                >
                  {i18n.t(k.DISCARD)}
                </Discard>
              ) : null}
              <Done
                data-cy={`checklist-text-done`}
                onClick={updateChecklistValue}
              >
                {i18n.t(k.DONE)}
              </Done>
            </TextFieldButtons>
          </TextField>
          <TextFieldUpdate
            lastMessage={lastMessage}
            modified={modified}
            edit={edit}
          />
        </TextFieldContainer>
      </OutsideClickHandler>
    );
  }

  if (R.isNil(value) || R.isEmpty(value)) {
    if (currentRoomId === roomId) {
      return (
        <AddData
          disabled={locked}
          type="text"
          handleClick={handleClick}
          isSelect={false}
          isMandatory={isMandatory}
        />
      );
    }

    // Show empty state on fields if they are default embedded fields
    return (
      <DisplayContainer isText={true} locked={locked} noBottomMargin={false}>
        <Display
          data-cy={`checklist-text-display-empty`}
          clickToEdit={true}
          disabled={locked}
          onClick={handleClick}
          isText={true}
          isMandatory={isMandatory}
        >
          <Linkify componentDecorator={componentDecorator}>
            {/* @ts-ignore: weird Chakra errors */}
            <ChakraText m={0} noOfLines={null} minH="24px" color="gray">
              {placeholder}
            </ChakraText>
          </Linkify>
          <IconContainer>
            <Icon type="checklistEdit" handleClick={openEdit} />
          </IconContainer>
        </Display>
      </DisplayContainer>
    );
  }

  return (
    <DisplayContainer isText={true} locked={locked} noBottomMargin={true}>
      <Display
        data-cy={`checklist-text-display`}
        clickToEdit={true}
        disabled={locked}
        onClick={handleClick}
        isText={true}
        isMandatory={isMandatory}
      >
        <Linkify componentDecorator={componentDecorator}>
          {/* @ts-ignore */}
          <ChakraText margin={0} minHeight="24px">
            {value}
          </ChakraText>
        </Linkify>
        <IconContainer>
          <Icon type="checklistEdit" />
        </IconContainer>
      </Display>
    </DisplayContainer>
  );
};

export default Text;
